// Customer health = computed signal derived from observable activity.
// No new schema needed: reads audit_log + open projects + open invoices.

export type HealthTone = 'green' | 'yellow' | 'red' | 'unknown';

export interface HealthSignal {
  tone: HealthTone;
  score: number; // 0-100
  reason: string;
}

interface HealthInputs {
  /** Days since most recent audit_log entry on company/deals/contacts/projects. */
  daysSinceLastActivity: number | null;
  /** Count of active (not paused/completed/cancelled) projects. */
  activeProjects: number;
  /** Count of stuck projects (active + no recent audit_log >14d). */
  stuckProjects: number;
  /** Count of paid invoices in the last 90 days. */
  paidInvoices90d: number;
  /** Count of invoices with status='sent' and not yet paid. */
  openInvoices: number;
}

export function computeHealth(inputs: HealthInputs): HealthSignal {
  const { daysSinceLastActivity, activeProjects, stuckProjects, paidInvoices90d, openInvoices } = inputs;

  if (daysSinceLastActivity === null) {
    return { tone: 'unknown', score: 0, reason: 'אין מספיק מידע' };
  }

  // Stuck projects override everything — red.
  if (stuckProjects > 0) {
    return {
      tone: 'red',
      score: 20,
      reason: `${String(stuckProjects)} פרויקטים תקועים`,
    };
  }

  // 60+ days no activity = red.
  if (daysSinceLastActivity >= 60) {
    return {
      tone: 'red',
      score: 25,
      reason: `אין פעילות ${String(daysSinceLastActivity)} ימים`,
    };
  }

  // 30-59 days no activity = yellow (warning).
  if (daysSinceLastActivity >= 30) {
    return {
      tone: 'yellow',
      score: 55,
      reason: `${String(daysSinceLastActivity)} ימים מאז פעילות אחרונה`,
    };
  }

  // Open invoices > 30 days = yellow.
  if (openInvoices > 0 && daysSinceLastActivity >= 14) {
    return {
      tone: 'yellow',
      score: 60,
      reason: `${String(openInvoices)} חשבוניות פתוחות + שקט מהלקוח`,
    };
  }

  // Healthy: recent activity, paid invoices, active engagement.
  if (paidInvoices90d > 0 || activeProjects > 0) {
    return {
      tone: 'green',
      score: 85,
      reason:
        activeProjects > 0
          ? `${String(activeProjects)} פרויקטים פעילים`
          : `${String(paidInvoices90d)} חשבוניות שולמו ב-90 הימים האחרונים`,
    };
  }

  // Default green — recent activity but no other strong signals.
  return { tone: 'green', score: 75, reason: 'בקשר' };
}
